import type { QueryClient } from "@tanstack/react-query";
import type { WsNotificationEvent } from "@/types/notification";

/**
 * Silent order_update reasons sent when the server moves an order's stage, or
 * frees a table (`table_freed`: the session ended, an empty or all-cancelled
 * order closed, its KOTs cancelled).
 */
export const ORDER_STAGE_REASONS = new Set(["food_ready", "handed_to_rider", "completed", "table_freed"]);

/** Queries that show an order's stage: the order, tables, active and history lists. */
export const ORDER_STAGE_QUERY_KEYS = [
  "order",
  "active-orders",
  "orders-by-type",
  "order-history",
  "tables-with-areas",
  "tables-summary",
  "table-session",
] as const;

export function isOrderStageEvent(event: Pick<WsNotificationEvent, "type" | "metadata">): boolean {
  if (event.type !== "order_update") return false;
  const reason = (event.metadata as { reason?: unknown } | null)?.reason;
  return typeof reason === "string" && ORDER_STAGE_REASONS.has(reason);
}

/** order_update (food_ready / handed_to_rider / completed / table_freed): refetch the order and table views. */
export function invalidateOnOrderStageEvent(
  queryClient: QueryClient,
  event: Pick<WsNotificationEvent, "type" | "metadata">,
): boolean {
  if (!isOrderStageEvent(event)) return false;
  for (const key of ORDER_STAGE_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key], exact: false });
  }
  // A freed table's KOTs were cancelled: the kitchen display drops them.
  if ((event.metadata as { reason?: unknown } | null)?.reason === "table_freed") {
    queryClient.invalidateQueries({ queryKey: ["kds-tickets"], exact: false });
  }
  return true;
}
